/* =========================================================================
   SightForest — global.js  (vanilla, no dependencies)
   ========================================================================= */
(function () {
  'use strict';

  var SF = window.SightForest || {};
  var routes = SF.routes || {};
  var sdConfigCache = null;

  function sdConfig() {
    if (sdConfigCache) return sdConfigCache;
    var node = document.querySelector('[data-sd-bundle-config]');
    if (!node) return null;
    try { sdConfigCache = JSON.parse(node.textContent || '{}'); } catch (e) { return null; }
    return sdConfigCache;
  }

  function money(cents) {
    // Lightweight money formatter using shop money_format. Falls back gracefully.
    try {
      var amount = (cents / 100).toFixed(2);
      var fmt = SF.moneyFormat || '${{amount}}';
      return fmt.replace(/\{\{\s*amount\s*\}\}/, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
    } catch (e) { return (cents / 100).toFixed(2); }
  }

  function embedVideo(url) {
    if (!url) return '';
    var yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
    if (yt) return '<iframe src="https://www.youtube.com/embed/' + yt[1] + '?autoplay=1&rel=0&playsinline=1" title="Video" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>';
    var vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vm) return '<iframe src="https://player.vimeo.com/video/' + vm[1] + '?autoplay=1" title="Video" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>';
    return '<video src="' + url.replace(/"/g, '') + '" autoplay controls playsinline></video>';
  }

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }

  function isEligibleCameraProduct(productId) {
    var cfg = sdConfig();
    if (!cfg) return false;
    return (cfg.cameraProductIds || []).map(String).indexOf(String(productId)) !== -1;
  }

  function buildSdBundleLine(capacity, quantity) {
    var cfg = sdConfig();
    var entry = cfg && cfg.capacities && cfg.capacities[capacity];
    if (!entry || !entry.variantId) return null;
    var properties = {
      _bundle_role: 'sd',
      _bundle_gift: capacity === '32GB' ? 'true' : 'false',
      _bundle_sd_capacity: capacity,
      _bundle_camera_quantity: String(quantity),
      _bundle_parent: 'sightforest-cameras',
      _bundle_version: '2'
    };
    if (capacity === '32GB') properties._gift = 'true';
    return { id: parseInt(entry.variantId, 10), quantity: quantity, properties: properties };
  }

  // Build cart items for a PDP add/buy button: pack math + paid add-ons + protected SD bundle line.
  function buildBundleItems(btn) {
    var form = btn.closest('[data-product-form]');
    var qi = form ? form.querySelector('[data-qty-input]') : null;
    var packSize = parseInt(btn.getAttribute('data-pack-size'), 10) || 1;
    var units = qi ? ((parseInt(qi.value, 10) || 1) * packSize) : (parseInt(btn.getAttribute('data-quantity'), 10) || 1);
    var items = [{ id: parseInt(btn.getAttribute('data-variant-id'), 10), quantity: units }];
    if (form) {
      Array.prototype.slice.call(form.querySelectorAll('[data-addon]:checked')).forEach(function (cb) {
        var vid = cb.getAttribute('data-variant-id');
        items.push({ id: parseInt(vid, 10), quantity: 1 });
      });
      var productId = form.getAttribute('data-product-id');
      var cfg = sdConfig();
      var minimum = cfg ? (parseInt(cfg.minimumCameraQuantity, 10) || 2) : 2;
      if (isEligibleCameraProduct(productId)) {
        var selected = form.querySelector('[data-sd-capacity]:checked');
        var selectedCapacity = selected ? selected.value : 'none';
        if (units >= minimum) {
          var sdLine = buildSdBundleLine(selectedCapacity === 'none' ? '32GB' : selectedCapacity, units);
          if (sdLine) items.push(sdLine);
        } else if (units === 1 && selectedCapacity !== 'none' && cfg.capacities && cfg.capacities[selectedCapacity]) {
          items.push({ id: parseInt(cfg.capacities[selectedCapacity].variantId, 10), quantity: 1 });
        }
      }
    }
    return items;
  }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function fetchJSON(url, opts) {
    return fetch(url, opts).then(function (r) { return r.json(); });
  }

  /* Serialize every cart write. Shopify's cart AJAX API is not concurrency-safe,
     so overlapping change/add/gift-sync calls can return an inconsistent cart.
     Each task runs only after the previous one settles. */
  var cartQueue = Promise.resolve();
  function enqueueCart(task) {
    var run = cartQueue.then(task, task);
    cartQueue = run.then(function () {}, function () {});
    return run;
  }
  var lineMutationStates = {};

  function matchingCartLines(key) {
    return $all('.cart-line').filter(function (line) { return line.getAttribute('data-line-key') === key; });
  }

  function updateOptimisticCartCount(delta) {
    if (!delta) return;
    $all('[data-cart-count]').forEach(function (el) {
      var current = parseInt(el.textContent, 10) || 0;
      var next = Math.max(0, current + delta);
      el.textContent = next;
      el.setAttribute('data-count', next);
    });
  }

  function renderOptimisticLineQuantity(key, quantity) {
    matchingCartLines(key).forEach(function (line) {
      line.classList.add('is-optimistic');
      line.classList.toggle('is-removing', quantity === 0);
      var input = line.querySelector('[data-line-input]');
      if (input) input.value = quantity;
      var unitPrice = parseInt(line.getAttribute('data-unit-price'), 10) || 0;
      var total = line.querySelector('.cart-line__total');
      // Camera bundle discounts change with quantity. Never flash a higher,
      // mathematically-wrong total while Shopify calculates the exact tier price.
      if (total && unitPrice && line.getAttribute('data-optimistic-total') !== 'false') {
        total.textContent = money(unitPrice * quantity);
      }
    });
  }

  function reapplyOptimisticLineQuantities() {
    Object.keys(lineMutationStates).forEach(function (key) {
      renderOptimisticLineQuantity(key, lineMutationStates[key].desired);
    });
  }

  function flushLineChange(key) {
    var state = lineMutationStates[key];
    if (!state || state.inFlight) return Promise.resolve();
    state.inFlight = true;
    state.timer = null;
    var sentQuantity = state.desired;
    var updates = {};
    updates[key] = sentQuantity;
    return enqueueCart(function () {
      return fetchJSON(routes.cartUpdate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ updates: updates, sections: Cart.sectionRequestList(), sections_url: window.location.pathname })
      });
    }).then(function (res) {
      state.inFlight = false;
      if (res.status) throw new Error(res.description || res.message || 'Cart update failed');
      if (state.desired !== sentQuantity) {
        flushLineChange(key);
        return res;
      }
      delete lineMutationStates[key];
      Cart.applySections(res.sections);
      Cart.updateBubble(res);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: res }));
      return res;
    }).catch(function (error) {
      state.inFlight = false;
      delete lineMutationStates[key];
      document.dispatchEvent(new CustomEvent('cart:error', { detail: error }));
      return Cart.refresh();
    });
  }

  function scheduleLineChange(key, quantity, immediate) {
    if (!key) return;
    var lines = matchingCartLines(key);
    var fallback = lines[0] ? (parseInt(lines[0].getAttribute('data-server-quantity'), 10) || 0) : 0;
    var state = lineMutationStates[key] || { desired: fallback, inFlight: false, timer: null };
    var previous = state.desired;
    state.desired = quantity;
    lineMutationStates[key] = state;
    renderOptimisticLineQuantity(key, quantity);
    updateOptimisticCartCount(quantity - previous);
    if (state.timer) clearTimeout(state.timer);
    if (immediate) flushLineChange(key);
    else state.timer = setTimeout(function () { flushLineChange(key); }, 120);
  }

  /* ---------------------------------------------------------------------
     Cart store — fetch state, add/change lines, re-render via Section API
     --------------------------------------------------------------------- */
  var Cart = {
    sectionsToRender: function () {
      var ids = ['cart-drawer', 'cart-icon-bubble', 'main-cart', 'free-shipping-bar'];
      return ids.filter(function (id) { return document.getElementById('shopify-section-' + id) || document.querySelector('[data-cart-section="' + id + '"]'); });
    },

    add: function (items, opts) {
      opts = opts || {};
      var body = { items: items, sections: this.sectionRequestList(), sections_url: window.location.pathname };
      return enqueueCart(function () {
        return fetchJSON(routes.cartAdd, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        }).then(function (res) {
          if (res.status) {
            document.dispatchEvent(new CustomEvent('cart:error', { detail: res }));
            throw new Error(res.description || res.message || 'Cart update failed');
          }
          Cart.applySections(res.sections);
          if (opts.openDrawer !== false && SF.cartType === 'drawer') Drawer.open();
          document.dispatchEvent(new CustomEvent('cart:added', { detail: res }));
          fetchJSON(routes.cart + '.js?t=' + Date.now()).then(function (cart) { Cart.updateBubble(cart); }).catch(function () {});
          return res;
        });
      });
    },

    // Change a stable cart-line key via /cart/update.js. The serialized SD
    // reconciliation runs after the cart event and never touches paid SD lines.
    changeLine: function (key, quantity) {
      if (typeof key !== 'string') return Cart.change(key, quantity);
      var updates = {};
      updates[key] = quantity;
      return enqueueCart(function () {
        var body = { updates: updates, sections: Cart.sectionRequestList(), sections_url: window.location.pathname };
        return fetchJSON(routes.cartUpdate, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        }).then(function (res) {
          Cart.applySections(res.sections);
          Cart.updateBubble(res);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: res }));
          return res;
        });
      });
    },

    // lineOrKey: a line key string (preferred — stable when gift lines shift) or a 1-based index.
    change: function (lineOrKey, quantity) {
      return enqueueCart(function () {
        var body = { quantity: quantity, sections: Cart.sectionRequestList(), sections_url: window.location.pathname };
        if (typeof lineOrKey === 'string') body.id = lineOrKey; else body.line = lineOrKey;
        return fetchJSON(routes.cartChange, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        }).then(function (res) {
          Cart.applySections(res.sections);
          Cart.updateBubble(res);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: res }));
          return res;
        });
      });
    },

    sectionRequestList: function () {
      var list = [];
      $all('[data-cart-section]').forEach(function (el) {
        var id = el.getAttribute('data-cart-section');
        if (list.indexOf(id) === -1) list.push(id);
      });
      return list;
    },

    refresh: function () {
      return fetchJSON(routes.cart + '?sections=' + this.sectionRequestList().join(',') + '&t=' + Date.now())
        .then(function (sections) {
          Cart.applySections(sections);
          return fetchJSON(routes.cart + '.js');
        })
        .then(function (cart) { Cart.updateBubble(cart); return cart; });
    },

    applySections: function (sections) {
      if (!sections) return;
      Object.keys(sections).forEach(function (id) {
        $all('[data-cart-section="' + id + '"]').forEach(function (target) {
          var html = new DOMParser().parseFromString(sections[id], 'text/html');
          var fresh = html.querySelector('[data-cart-section="' + id + '"]') || html.body;
          target.innerHTML = fresh.innerHTML;
        });
      });
      bindAll(document);
      refreshSdUpgradeMoney();
      reapplyOptimisticLineQuantities();
      reapplyPendingSdCapacity();
    },

    updateBubble: function (cartOrCount) {
      // Accept a cart object (preferred — excludes free gift lines) or a raw number.
      var count = cartOrCount;
      if (cartOrCount && typeof cartOrCount === 'object' && cartOrCount.items) {
        count = cartOrCount.items.reduce(function (n, it) { return n + (isInternalSdLine(it) ? 0 : it.quantity); }, 0);
      }
      $all('[data-cart-count]').forEach(function (el) {
        el.textContent = count;
        el.setAttribute('data-count', count);
      });
    }
  };
  window.SFCart = Cart;

  /* ---------------------------------------------------------------------
     SD bundle synchronization. Paid SD lines are never touched; only v2
     bundle lines and reliably marked legacy gift lines are reconciled.
     --------------------------------------------------------------------- */
  var sdSyncing = false;
  var requestedSdCapacity = null;
  var activeSdCapacity = null;
  var requestedSdSource = 'cart_drawer';
  var sdSelectionTimer = null;

  function propertyValue(item, key) {
    return item && item.properties && item.properties[key] != null ? String(item.properties[key]) : '';
  }

  function isInternalSdLine(item) {
    return propertyValue(item, '_bundle_role') === 'sd'
      || propertyValue(item, '_gift').toLowerCase() === 'true';
  }

  function sdLineCapacity(item) {
    if (propertyValue(item, '_bundle_version') === '2' && propertyValue(item, '_bundle_role') === 'sd') {
      return propertyValue(item, '_bundle_sd_capacity') || '32GB';
    }
    return propertyValue(item, '_gift').toLowerCase() === 'true' ? '32GB' : null;
  }

  function setSdStatus(message, tone) {
    $all('[data-sd-status]').forEach(function (el) {
      el.textContent = message || '';
      el.classList.toggle('is-success', tone === 'success');
      el.classList.toggle('is-error', tone === 'error');
    });
  }

  function renderSdCapacityChoice(capacity, pending) {
    if (!capacity) return;
    $all('[data-sd-bundle-capacity]').forEach(function (button) {
      var selected = button.getAttribute('data-sd-bundle-capacity') === capacity;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-checked', String(selected));
    });
    $all('[data-sd-upsell]').forEach(function (upsell) {
      upsell.classList.toggle('is-pending', !!pending);
    });
    if (pending) {
      var strings = (sdConfig() && sdConfig().strings) || {};
      setSdStatus([strings.upgradeSelected, capacity].filter(Boolean).join(' · '), 'success');
    }
  }

  function reapplyPendingSdCapacity() {
    var capacity = requestedSdCapacity || activeSdCapacity;
    if (capacity) renderSdCapacityChoice(capacity, true);
  }

  function clearSdBundleLoading() {
    $all('.sd-upsell__choices.is-loading').forEach(function (g) { g.classList.remove('is-loading'); });
    $all('.sd-upsell__choice.is-busy').forEach(function (b) { b.classList.remove('is-busy'); });
  }

  function queueSdCapacityChoice(capacity, source) {
    requestedSdCapacity = capacity;
    requestedSdSource = source || 'cart_drawer';
    renderSdCapacityChoice(capacity, true);
    // Immediate loading feedback (spinner on the chosen option, others dimmed) so the ~1–2s
    // cart round-trip doesn't feel like nothing happened. Reuses the swap's is-loading/is-busy.
    $all('[data-sd-upsell]').forEach(function (upsell) {
      var group = upsell.querySelector('.sd-upsell__choices');
      if (group) group.classList.add('is-loading');
      var sel = upsell.querySelector('[data-sd-bundle-capacity="' + capacity + '"]');
      if (sel) sel.classList.add('is-busy');
    });
    if (sdSelectionTimer) clearTimeout(sdSelectionTimer);
    if (sdSyncing) return;
    sdSelectionTimer = setTimeout(function () {
      sdSelectionTimer = null;
      syncSdBundle({ source: requestedSdSource });
    }, 60);
  }

  function publishSdEvent(name, data) {
    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
        var cfg = sdConfig() || {};
        Shopify.analytics.publish(name, Object.assign({
          currency: cfg.activeCurrency,
          market: cfg.market,
          locale: cfg.locale
        }, data || {}));
      }
    } catch (e) {}
  }

  function syncSdBundle(options) {
    options = options || {};
    if (options.capacity) requestedSdCapacity = options.capacity;
    if (sdSyncing) return Promise.resolve();
    var cfg = sdConfig();
    if (!cfg || !cfg.capacities) return Promise.resolve();
    sdSyncing = true;
    var requested = requestedSdCapacity;
    requestedSdCapacity = null;
    activeSdCapacity = requested;
    var previousCapacity = null;
    var cameraQuantity = 0;

    return fetchJSON(routes.cart + '.js?t=' + Date.now()).then(function (cart) {
      var bundleLines = [];
      var paidBaseCardLines = [];
      cart.items.forEach(function (item) {
        if (isInternalSdLine(item)) bundleLines.push(item);
        else if (isEligibleCameraProduct(item.product_id)) cameraQuantity += item.quantity;
        else if (cfg.capacities['32GB'] && String(item.variant_id) === String(cfg.capacities['32GB'].variantId)) paidBaseCardLines.push(item);
      });
      var modern = bundleLines.find(function (item) { return propertyValue(item, '_bundle_version') === '2'; });
      previousCapacity = modern ? sdLineCapacity(modern) : (bundleLines[0] ? sdLineCapacity(bundleLines[0]) : null);
      var capacity = requested || previousCapacity || '32GB';
      if (!cfg.capacities[capacity]) capacity = '32GB';
      var eligible = cameraQuantity >= (parseInt(cfg.minimumCameraQuantity, 10) || 2);
      var wanted = eligible ? cfg.capacities[capacity] : null;
      var keep = null;
      var updates = {};
      // A paid standalone 32GB card is pointless once the bundle includes free 32GB
      // cards (2+ cameras) — drop it so the customer isn't charged for what's included.
      if (eligible) paidBaseCardLines.forEach(function (l) { updates[l.key] = 0; });

      bundleLines.forEach(function (item) {
        var isModernMatch = wanted
          && propertyValue(item, '_bundle_version') === '2'
          && propertyValue(item, '_bundle_role') === 'sd'
          && sdLineCapacity(item) === capacity
          && String(item.variant_id) === String(wanted.variantId)
          && !keep;
        if (isModernMatch) {
          keep = item;
          if (item.quantity !== cameraQuantity) updates[item.key] = cameraQuantity;
        } else {
          updates[item.key] = 0;
        }
      });

      var needsAdd = !!wanted && !keep;
      if (!Object.keys(updates).length && !needsAdd) return cart;

      var sdSections = Cart.sectionRequestList();
      return enqueueCart(function () {
        var chain = Promise.resolve();
        if (Object.keys(updates).length) {
          chain = chain.then(function () {
            var upBody = { updates: updates };
            // Fold the rendered sections into the last mutation (add.js if present, else this one)
            // so we skip Cart.refresh()'s separate sections fetch — the swap feels much snappier.
            if (!needsAdd) { upBody.sections = sdSections; upBody.sections_url = routes.cart; }
            return fetchJSON(routes.cartUpdate, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify(upBody)
            });
          });
        }
        if (needsAdd) {
          chain = chain.then(function () {
            return fetchJSON(routes.cartAdd, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ items: [buildSdBundleLine(capacity, cameraQuantity)], sections: sdSections, sections_url: routes.cart })
            });
          });
        }
        return chain;
      }).then(function (last) {
        if (last && last.sections) {
          Cart.applySections(last.sections);
          // Bubble refresh is cosmetic — fire and forget so the swap completes ~0.5s sooner.
          fetchJSON(routes.cart + '.js').then(function (c) { Cart.updateBubble(c); }).catch(function () {});
          return null;
        }
        return Cart.refresh();
      }).then(function (updatedCart) {
        var superseded = requestedSdCapacity && requestedSdCapacity !== capacity;
        if (requested && !superseded) {
          var strings = cfg.strings || {};
          var message = capacity === '32GB' ? strings.included : (strings.upgradedTo || strings.cardsUpgraded || '').replace('__CAPACITY__', capacity);
          renderSdCapacityChoice(capacity, false);
          setSdStatus(message || strings.upgradeSelected, 'success');
          publishSdEvent(capacity === '32GB' ? 'sd_upgrade_removed' : 'sd_upgrade_selected', {
            camera_quantity: cameraQuantity,
            sd_capacity: capacity,
            previous_sd_capacity: previousCapacity,
            upgrade_value: (cfg.capacities[capacity].upgradeBaseCents || 0) * cameraQuantity,
            source: options.source || 'cart_drawer'
          });
        }
        return updatedCart;
      });
    }).catch(function () {
      var strings = (cfg && cfg.strings) || {};
      setSdStatus([strings.unable, strings.tryAgain].filter(Boolean).join(' '), 'error');
      publishSdEvent('sd_upgrade_failed', { camera_quantity: cameraQuantity, source: options.source || 'cart_drawer' });
    }).then(function (result) {
      sdSyncing = false;
      activeSdCapacity = null;
      if (requestedSdCapacity) syncSdBundle({ source: requestedSdSource || options.source });
      else clearSdBundleLoading();
      return result;
    }, function (error) {
      sdSyncing = false;
      activeSdCapacity = null;
      clearSdBundleLoading();
      throw error;
    });
  }
  window.SFSyncGifts = syncSdBundle;
  window.SFSyncSdBundle = syncSdBundle;
  document.addEventListener('cart:added', function () { syncSdBundle(); });
  document.addEventListener('cart:updated', function () { syncSdBundle(); });
  window.addEventListener('pageshow', function () { syncSdBundle(); });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') syncSdBundle();
  });

  function refreshSdUpgradeMoney(quantity) {
    var rate = 1;
    try {
      if (window.Shopify && Shopify.currency && Number(Shopify.currency.rate) > 0) rate = Number(Shopify.currency.rate);
    } catch (e) {}
    $all('[data-sd-upgrade-money]').forEach(function (el) {
      var base = parseInt(el.getAttribute('data-base-cents'), 10) || 0;
      var fixed = el.hasAttribute('data-sd-fixed-quantity');
      var qty = fixed ? (parseInt(el.getAttribute('data-quantity'), 10) || 1) : (quantity || parseInt(el.getAttribute('data-quantity'), 10) || 1);
      el.setAttribute('data-quantity', qty);
      el.textContent = '+' + money(Math.round(base * qty * rate));
    });
    $all('[data-sd-upgrade-value]').forEach(function (el) {
      var unit = parseInt(el.getAttribute('data-unit-cents'), 10) || 0;
      var qty = quantity || parseInt(el.getAttribute('data-quantity'), 10) || 1;
      el.setAttribute('data-quantity', qty);
      el.textContent = money(Math.round(unit * qty * rate));
    });
    $all('[data-sd-public-money]').forEach(function (el) {
      var base = parseInt(el.getAttribute('data-base-cents'), 10) || 0;
      el.textContent = money(Math.round(base * rate));
    });
  }

  function sdCapacityMessage(template, capacity) {
    return String(template || '').replace('__CAPACITY__', capacity || '');
  }

  function renderPdpSdSelection(fieldset, input) {
    if (!fieldset || !input) return;
    $all('[data-sd-capacity-option]', fieldset).forEach(function (option) {
      var radio = option.querySelector('[data-sd-capacity]');
      option.classList.toggle('is-selected', !!radio && radio.checked);
    });
    var summary = fieldset.querySelector('[data-sd-capacity-summary]');
    var legend = fieldset.querySelector('[data-sd-capacity-legend]');
    var action = fieldset.querySelector('[data-sd-capacity-action]');
    var cfg = sdConfig() || {};
    var strings = cfg.strings || {};
    var mode = fieldset.getAttribute('data-sd-mode') || 'bundle';
    var quantity = parseInt(fieldset.getAttribute('data-camera-quantity'), 10) || 1;
    if (summary) {
      if (mode === 'single') {
        summary.textContent = input.value === 'none'
          ? (strings.singleHint || strings.noCardSummary || '')
          : sdCapacityMessage(strings.cardSelected, input.value);
      } else {
        summary.textContent = input.value === '32GB'
          ? (strings.upgradeIncluded || '')
          : (strings.upgradeSelected || '');
      }
    }
    if (legend) {
      if (mode === 'single') {
        legend.textContent = strings.singleTitle || strings.memoryCardOptional || '';
      } else if (input.value === '32GB') {
        legend.textContent = quantity + '× ' + (strings.free32Included || '32GB SD cards included');
      } else {
        legend.textContent = quantity + '× ' + input.value + ' ' + (strings.sdCards || 'SD cards');
      }
    }
    if (action) {
      if (mode === 'single') action.textContent = input.value === 'none' ? (strings.choose || '') : (strings.change || '');
      else action.textContent = input.value === '32GB' ? (strings.upgrade || '') : (strings.change || '');
    }
    var form = fieldset.closest('[data-product-form]');
    $all('[data-sd-quick-select]', form || document).forEach(function (button) {
      var selected = mode === 'single' && input.value === button.getAttribute('data-sd-quick-select');
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
  }

  function setPdpSdMode(fieldset, quantity, initialize) {
    if (!fieldset) return;
    var previousMode = fieldset.getAttribute('data-sd-mode') || 'bundle';
    var nextMode = quantity < 2 ? 'single' : 'bundle';
    var current = fieldset.querySelector('[data-sd-capacity]:checked');
    var wanted = nextMode === 'single' ? 'none' : '32GB';
    if (!initialize && previousMode === nextMode && current) wanted = current.value;
    else if (!initialize && previousMode === 'single' && nextMode === 'bundle' && current && current.value !== 'none') wanted = current.value;
    fieldset.setAttribute('data-sd-mode', nextMode);
    fieldset.setAttribute('data-camera-quantity', quantity);
    var target = fieldset.querySelector('[data-sd-capacity][value="' + wanted + '"]');
    if (target) target.checked = true;
    renderPdpSdSelection(fieldset, target || current);
  }

  function selectPdpSdCapacity(input, source) {
    if (!input) return;
    var fieldset = input.closest('[data-sd-capacity-selector]');
    if (!fieldset) return;
    var cfg = sdConfig() || {};
    renderPdpSdSelection(fieldset, input);
    publishSdEvent(input.value === 'none' ? 'sd_addon_declined' : 'sd_capacity_selected', {
      product_id: fieldset.closest('[data-product-form]') && fieldset.closest('[data-product-form]').getAttribute('data-product-id'),
      camera_quantity: parseInt(fieldset.getAttribute('data-camera-quantity'), 10) || 1,
      sd_capacity: input.value,
      upgrade_value: cfg.capacities && cfg.capacities[input.value] ? cfg.capacities[input.value].upgradeBaseCents : 0,
      source: source || 'product_bundle'
    });
  }

  function initializePdpSdSelectors(ctx) {
    $all('[data-sd-capacity-selector]', ctx).forEach(function (fieldset) {
      if (fieldset.__sfSdInitialized) return;
      fieldset.__sfSdInitialized = true;
      var quantity = parseInt(fieldset.getAttribute('data-camera-quantity'), 10) || 1;
      setPdpSdMode(fieldset, quantity, true);
      publishSdEvent('sd_bundle_selector_viewed', {
        product_id: fieldset.closest('[data-product-form]') && fieldset.closest('[data-product-form]').getAttribute('data-product-id'),
        camera_quantity: quantity,
        sd_capacity: quantity < 2 ? 'none' : '32GB',
        source: 'product_bundle'
      });
    });
  }

  document.addEventListener('product:selection', function (event) {
    var qty = event.detail && event.detail.packSize ? event.detail.packSize : 1;
    $all('[data-sd-capacity-selector]').forEach(function (selector) {
      setPdpSdMode(selector, qty, false);
    });
    refreshSdUpgradeMoney(qty);
  });

  /* ---------------------------------------------------------------------
     Cart drawer
     --------------------------------------------------------------------- */
  var Drawer = {
    open: function () { syncSdBundle({ source: 'cart_drawer' }); document.body.classList.add('cart-drawer-open'); document.body.style.overflow = 'hidden'; var d = $('.cart-drawer'); if (d) { d.removeAttribute('inert'); d.setAttribute('aria-hidden', 'false'); } },
    close: function () { document.body.classList.remove('cart-drawer-open'); document.body.style.overflow = ''; var d = $('.cart-drawer'); if (d) { d.setAttribute('inert', ''); d.setAttribute('aria-hidden', 'true'); } }
  };
  window.SFDrawer = Drawer;

  /* ---------------------------------------------------------------------
     Behaviors bound (delegation + per-element where needed)
     --------------------------------------------------------------------- */
  function bindAll(ctx) {
    bindCarousels(ctx);
    bindAccordions(ctx);
    bindDisclosures(ctx);
    initializePdpSdSelectors(ctx);
  }

  // ---- Global click delegation ----
  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-cart-open]');
    if (t) { e.preventDefault(); if (SF.cartType === 'drawer') { e.preventDefault(); Drawer.open(); } else { window.location.href = routes.cart; } return; }

    if (e.target.closest('[data-cart-close]') || e.target.closest('.drawer-overlay')) { Drawer.close(); return; }

    // Mobile nav toggle
    var burger = e.target.closest('[data-nav-toggle]');
    if (burger) {
      var menu = $('#HeaderMobileMenu');
      var willOpen = menu ? menu.hasAttribute('hidden') : false;
      if (menu && willOpen) {
        var mobileHeader = menu.closest('.header');
        var mobileHeaderTop = mobileHeader ? Math.max(0, mobileHeader.getBoundingClientRect().top) : 0;
        menu.style.setProperty('--mobile-nav-offset', '-' + mobileHeaderTop + 'px');
      }
      if (menu) menu.toggleAttribute('hidden', !willOpen);
      document.documentElement.classList.toggle('nav-open', willOpen);
      document.querySelectorAll('[data-nav-toggle]').forEach(function (toggle) {
        toggle.setAttribute('aria-expanded', String(willOpen));
      });
      return;
    }

    // Footer column accordion (mobile)
    var fhead = e.target.closest('.footer__col-toggle');
    if (fhead && window.matchMedia('(max-width: 760px)').matches) {
      fhead.closest('.footer__col').classList.toggle('is-open');
      return;
    }

    // Quantity steppers
    var step = e.target.closest('[data-qty-change]');
    if (step) {
      e.preventDefault();
      var wrap = step.closest('[data-qty-wrap]');
      var input = wrap ? wrap.querySelector('[data-qty-input]') : null;
      if (input) {
        var val = parseInt(input.value, 10) || 1;
        val += parseInt(step.getAttribute('data-qty-change'), 10);
        if (val < (parseInt(input.min, 10) || 1)) val = parseInt(input.min, 10) || 1;
        input.value = val;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    // Add to cart (data-add-to-cart with data-variant-id)
    var sdQuickSelect = e.target.closest('[data-sd-quick-select]');
    if (sdQuickSelect) {
      e.preventDefault();
      var quickForm = sdQuickSelect.closest('[data-product-form]');
      var quickCapacity = sdQuickSelect.getAttribute('data-sd-quick-select');
      var quickValue = sdQuickSelect.getAttribute('aria-pressed') === 'true' ? 'none' : quickCapacity;
      var quickInput = quickForm ? quickForm.querySelector('[data-sd-capacity][value="' + quickValue + '"]') : null;
      if (quickInput) {
        quickInput.checked = true;
        quickInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    var sdCapacityButton = e.target.closest('[data-sd-bundle-capacity]');
    if (sdCapacityButton) {
      e.preventDefault();
      var capacity = sdCapacityButton.getAttribute('data-sd-bundle-capacity');
      var source = sdCapacityButton.getAttribute('data-sd-source') || 'cart_drawer';
      publishSdEvent('sd_upsell_selected', { sd_capacity: capacity, source: source });
      queueSdCapacityChoice(capacity, source);
      return;
    }

    // Paid standalone SD card capacity swap (cart drawer / cart page). Delegated here
    // (not an inline snippet script) so it survives Cart.refresh() re-rendering the
    // cart via innerHTML — inline <script> tags don't execute when injected that way.
    var sdSwap = e.target.closest('[data-sd-swap]');
    if (sdSwap) {
      e.preventDefault();
      if (sdSwap.getAttribute('aria-checked') === 'true') return;
      var swapKey = sdSwap.getAttribute('data-line-key');
      var swapVariant = parseInt(sdSwap.getAttribute('data-target-variant'), 10);
      var swapQty = parseInt(sdSwap.getAttribute('data-qty'), 10) || 1;
      if (!swapKey || !swapVariant) return;
      var swapGroup = sdSwap.closest('.sd-upsell__choices');
      // Optimistic UI: highlight the chosen capacity + show a spinner instantly, so the
      // ~1s network round-trip doesn't feel like a frozen click.
      if (swapGroup) {
        swapGroup.classList.add('is-loading');
        $all('[data-sd-swap]', swapGroup).forEach(function (x) { x.classList.remove('is-selected', 'is-busy'); x.setAttribute('aria-checked', 'false'); });
        sdSwap.classList.add('is-selected', 'is-busy');
        sdSwap.setAttribute('aria-checked', 'true');
      }
      publishSdEvent('sd_upsell_selected', { source: sdSwap.getAttribute('data-sd-source') || 'cart', variant_id: swapVariant });
      // 2 requests instead of 4: add the new capacity, then remove the old line WITH the
      // full cart + rendered sections in the same response — no separate refresh fetch.
      // Locale-prefixed routes.cart (e.g. /de/cart) so the discount Function re-evaluates
      // in the buyer's language.
      fetch(routes.cart + '/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: swapVariant, quantity: swapQty }) })
        .then(function () {
          return fetch(routes.cart + '/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: swapKey, quantity: 0, sections: Cart.sectionRequestList(), sections_url: routes.cart }) });
        })
        .then(function (r) { return r.json(); })
        .then(function (cart) { Cart.applySections(cart.sections); Cart.updateBubble(cart); })
        .catch(function () { if (swapGroup) swapGroup.classList.remove('is-loading'); Cart.refresh(); });
      return;
    }

    var addBtn = e.target.closest('[data-add-to-cart]');
    if (addBtn && addBtn.getAttribute('data-variant-id')) {
      e.preventDefault();
      var addForm = addBtn.closest('[data-product-form]');
      var addError = addForm ? addForm.querySelector('[data-product-error]') : null;
      if (addError) {
        addError.hidden = true;
        addError.textContent = '';
      }
      var items = buildBundleItems(addBtn);
      var original = addBtn.innerHTML;
      addBtn.setAttribute('disabled', 'disabled');
      addBtn.innerHTML = SF.strings ? SF.strings.adding : 'Adding…';
      Cart.add(items).then(function () {
        addBtn.removeAttribute('disabled');
        addBtn.innerHTML = original;
        if (addBtn.hasAttribute('data-sd-upsell-add')) {
          var cfg = sdConfig() || {};
          setSdStatus((cfg.strings && cfg.strings.added) || '', 'success');
          publishSdEvent('sd_upsell_added', {
            variant_id: addBtn.getAttribute('data-variant-id'),
            sd_capacity: addBtn.getAttribute('data-sd-capacity-value'),
            source: addBtn.getAttribute('data-sd-source') || 'cart_drawer'
          });
        }
      }).catch(function () {
        addBtn.removeAttribute('disabled');
        addBtn.innerHTML = original;
        if (addError) {
          addError.textContent = (SF.strings && SF.strings.cartError) || 'This item could not be added. Please check delivery availability or try again.';
          addError.hidden = false;
        }
        if (addBtn.hasAttribute('data-sd-upsell-add')) {
          var cfg = sdConfig() || {};
          var strings = cfg.strings || {};
          setSdStatus([strings.unable, strings.tryAgain].filter(Boolean).join(' '), 'error');
          publishSdEvent('sd_upgrade_failed', {
            variant_id: addBtn.getAttribute('data-variant-id'),
            sd_capacity: addBtn.getAttribute('data-sd-capacity-value'),
            source: addBtn.getAttribute('data-sd-source') || 'cart_drawer'
          });
        }
      });
      return;
    }

    // Buy it now — add current selection then go straight to checkout
    var buyNow = e.target.closest('[data-buy-now]');
    if (buyNow && buyNow.getAttribute('data-variant-id')) {
      e.preventDefault();
      var buyForm = buyNow.closest('[data-product-form]');
      var buyError = buyForm ? buyForm.querySelector('[data-product-error]') : null;
      if (buyError) {
        buyError.hidden = true;
        buyError.textContent = '';
      }
      var bnItems = buildBundleItems(buyNow);
      buyNow.setAttribute('disabled', 'disabled');
      Cart.add(bnItems, { openDrawer: false }).then(function () { window.location.href = '/checkout'; })
        .catch(function () {
          buyNow.removeAttribute('disabled');
          if (buyError) {
            buyError.textContent = (SF.strings && SF.strings.cartError) || 'This item could not be added. Please check delivery availability or try again.';
            buyError.hidden = false;
          }
        });
      return;
    }

    // Cart line stepper (+/−): relative, optimistic, debounced, keyed by stable line key.
    var stepBtn = e.target.closest('[data-line-step]');
    if (stepBtn) {
      e.preventDefault();
      var sLine = stepBtn.closest('.cart-line');
      if (!sLine) return;
      var sKey = sLine.getAttribute('data-line-key');
      var sInput = sLine.querySelector('[data-line-input]');
      var delta = parseInt(stepBtn.getAttribute('data-line-step'), 10) || 0;
      var existingState = lineMutationStates[sKey];
      var cur = existingState ? existingState.desired : (sInput ? (parseInt(sInput.value, 10) || 0) : 0);
      var next = Math.max(0, cur + delta);
      scheduleLineChange(sKey, next);
      return;
    }

    // Cart line remove (absolute qty, e.g. 0): immediate, keyed by line key.
    var lineBtn = e.target.closest('[data-line-change]');
    if (lineBtn) {
      e.preventDefault();
      var lineEl = lineBtn.closest('.cart-line');
      var key = lineEl ? lineEl.getAttribute('data-line-key') : null;
      var newQty = parseInt(lineBtn.getAttribute('data-line-change'), 10);
      scheduleLineChange(key, newQty, true);
      return;
    }

    // Review video — click play → load the real player into the media box
    var playBtn = e.target.closest('[data-review-play]');
    if (playBtn) {
      e.preventDefault();
      var vurl = playBtn.getAttribute('data-video-url');
      var vmedia = playBtn.closest('.media, .rvx-card__media');
      if (vurl && vmedia) vmedia.innerHTML = embedVideo(vurl);
      return;
    }

    // Cookie banner — re-open from the footer "Manage preferences" link (lets shoppers change their choice later)
    var reopenBtn = e.target.closest('[data-cookie-reopen]');
    if (reopenBtn) {
      e.preventDefault();
      var rb = document.getElementById('CookieBanner');
      if (rb) {
        rb.style.display = '';
        var rmain = rb.querySelector('[data-cookie-main]'); if (rmain) rmain.hidden = true;
        var rpanel = rb.querySelector('[data-cookie-panel]'); if (rpanel) rpanel.hidden = false;
        try {
          var cur = (window.Shopify && Shopify.customerPrivacy && Shopify.customerPrivacy.currentVisitorConsent && Shopify.customerPrivacy.currentVisitorConsent()) || {};
          var tg = rb.querySelectorAll('[data-consent]');
          for (var k = 0; k < tg.length; k++) {
            var c2 = tg[k].getAttribute('data-consent');
            var val = c2 === 'marketing' ? cur.marketing : cur.analytics;
            tg[k].checked = (val === 'yes' || val === true);
          }
        } catch (err) {}
      }
      return;
    }
    // Cookie banner — open the granular preferences panel
    var prefsBtn = e.target.closest('[data-cookie-prefs]');
    if (prefsBtn) {
      var bp = document.getElementById('CookieBanner');
      if (bp) {
        var main = bp.querySelector('[data-cookie-main]');
        var panel = bp.querySelector('[data-cookie-panel]');
        if (main) main.hidden = true;
        if (panel) panel.hidden = false;
      }
      return;
    }
    // Cookie banner — apply consent (accept all / reject all / save preferences) via the Customer Privacy API
    var cookieBtn = e.target.closest('[data-cookie-dismiss]');
    var saveBtn = e.target.closest('[data-cookie-save]');
    if (cookieBtn || saveBtn) {
      var consent;
      if (saveBtn) {
        consent = { analytics: false, marketing: false, preferences: false, sale_of_data: false };
        var bn = document.getElementById('CookieBanner');
        if (bn) {
          var toggles = bn.querySelectorAll('[data-consent]');
          for (var i = 0; i < toggles.length; i++) {
            var cat = toggles[i].getAttribute('data-consent');
            var on = !!toggles[i].checked;
            if (cat === 'analytics') { consent.analytics = on; consent.preferences = on; }
            if (cat === 'marketing') { consent.marketing = on; consent.sale_of_data = on; }
          }
        }
        try { localStorage.setItem('sf_cookie_choice', 'custom'); } catch (err) {}
      } else {
        var grant = cookieBtn.getAttribute('data-cookie-dismiss') === 'accepted';
        consent = { analytics: grant, marketing: grant, preferences: grant, sale_of_data: grant };
        try { localStorage.setItem('sf_cookie_choice', grant ? 'accepted' : 'declined'); } catch (err) {}
      }
      try {
        if (window.Shopify && Shopify.customerPrivacy && Shopify.customerPrivacy.setTrackingConsent) {
          Shopify.customerPrivacy.setTrackingConsent(consent, function () {});
        }
      } catch (err) {}
      var banner = $('.cookie-banner'); if (banner) banner.style.display = 'none';
      return;
    }
  });

  // ---- Cart line qty input change ----
  document.addEventListener('change', function (e) {
    var sdCapacityInput = e.target.closest('[data-sd-capacity]');
    if (sdCapacityInput) selectPdpSdCapacity(sdCapacityInput, 'product_bundle');

    var lineInput = e.target.closest('[data-line-input]');
    if (lineInput) {
      var lineWrap = lineInput.closest('.cart-line');
      var key = lineWrap ? lineWrap.getAttribute('data-line-key') : null;
      var q = parseInt(lineInput.value, 10);
      if (isNaN(q) || q < 0) q = 0;
      scheduleLineChange(key, q);
    }
  });

  /* ---------------------------------------------------------------------
     Carousels (prev / next scroll)
     --------------------------------------------------------------------- */
  function bindCarousels(ctx) {
    $all('[data-carousel]', ctx).forEach(function (root) {
      if (root.__sfBound) return; root.__sfBound = true;
      var track = root.querySelector('[data-carousel-track]') || root;
      var navs = $all('[data-carousel-prev], [data-carousel-next]', root);
      $all('[data-carousel-prev]', root).forEach(function (b) { b.addEventListener('click', function () { scrollByCard(track, -1); }); });
      $all('[data-carousel-next]', root).forEach(function (b) { b.addEventListener('click', function () { scrollByCard(track, 1); }); });
      // Hide the arrows when everything already fits (no overflow to scroll).
      // Geometry reads batched into rAF so init/resize never force a synchronous reflow.
      var syncQueued = false;
      function syncNav() {
        if (syncQueued) return; syncQueued = true;
        requestAnimationFrame(function () {
          syncQueued = false;
          var overflow = track.scrollWidth > track.clientWidth + 2;
          navs.forEach(function (n) { n.style.display = overflow ? '' : 'none'; });
        });
      }
      syncNav();
      window.addEventListener('resize', syncNav);
      if (window.ResizeObserver) { try { new ResizeObserver(syncNav).observe(track); } catch (e) {} }
    });
  }
  function scrollByCard(track, dir) {
    var first = track.querySelector(':scope > *');
    var amount = first ? (first.getBoundingClientRect().width + 20) : track.clientWidth * 0.8;
    track.scrollBy({ left: amount * dir, behavior: 'smooth' });
  }

  /* ---------------------------------------------------------------------
     Accordions (animated, accessible)
     --------------------------------------------------------------------- */
  function bindAccordions(ctx) {
    $all('[data-accordion-trigger]', ctx).forEach(function (btn) {
      if (btn.__sfBound) return; btn.__sfBound = true;
      btn.addEventListener('click', function () {
        var item = btn.closest('.accordion__item');
        var panel = item.querySelector('.accordion__panel');
        var open = item.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', String(open));
        if (panel) panel.style.maxHeight = open ? panel.scrollHeight + 'px' : '0px';
      });
    });
  }

  /* ---------------------------------------------------------------------
     Disclosures (language / country selectors)
     --------------------------------------------------------------------- */
  function bindDisclosures(ctx) {
    $all('[data-disclosure]', ctx).forEach(function (root) {
      if (root.__sfBound) return; root.__sfBound = true;
      var btn = root.querySelector('[data-disclosure-button]');
      if (!btn) return;
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var willOpen = !root.classList.contains('is-open');
        $all('[data-disclosure].is-open').forEach(function (o) { o.classList.remove('is-open'); });
        root.classList.toggle('is-open', willOpen);
        btn.setAttribute('aria-expanded', String(willOpen));
      });
    });
  }
  document.addEventListener('click', function (e) {
    if (!e.target.closest('[data-disclosure]')) {
      $all('[data-disclosure].is-open').forEach(function (o) { o.classList.remove('is-open'); });
    }
  });
  document.addEventListener('keyup', function (e) {
    if (e.key === 'Escape') {
      $all('[data-disclosure].is-open').forEach(function (o) { o.classList.remove('is-open'); });
      if (document.body.classList.contains('cart-drawer-open')) Drawer.close();
      var mobileMenu = $('#HeaderMobileMenu');
      if (mobileMenu && !mobileMenu.hasAttribute('hidden')) {
        mobileMenu.setAttribute('hidden', '');
        document.documentElement.classList.remove('nav-open');
        document.querySelectorAll('[data-nav-toggle]').forEach(function (toggle) {
          toggle.setAttribute('aria-expanded', 'false');
        });
        var mobileMenuButton = $('.header__burger');
        if (mobileMenuButton) mobileMenuButton.focus();
      }
    }
  });

  /* ---------------------------------------------------------------------
     Welcome offer — capture email (popup + footer) → subscribe to Klaviyo,
     flag the cart with `welcome_discount` so the discount Function takes
     20% off in-cart, then reveal the code. Consent-aware.
     --------------------------------------------------------------------- */
  var KLAVIYO_COMPANY = 'T9GGMQ';
  var KLAVIYO_LIST = 'WZCJVG';
  var PROMO_CODE = 'WELCOME10';

  function promoClaimed() { try { return localStorage.getItem('sf_promo_claimed') === '1'; } catch (e) { return false; } }

  // Storefront language (2-letter) — tags the Klaviyo profile so emails render in the right language.
  function sfLocale() {
    try {
      var l = (window.Shopify && Shopify.locale) || document.documentElement.lang || 'en';
      return String(l).toLowerCase().slice(0, 2);
    } catch (e) { return 'en'; }
  }

  function klaviyoSubscribe(email) {
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_COMPANY, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Welcome 10% form',
            profile: { data: { type: 'profile', attributes: { email: email, properties: { lang: sfLocale() } } } }
          },
          relationships: { list: { data: { type: 'list', id: KLAVIYO_LIST } } }
        }
      })
    });
  }

  function SFClaimPromo(email) {
    klaviyoSubscribe(email).catch(function () {});
    try {
      if (window.klaviyo && window.klaviyo.push) {
        window.klaviyo.push(['identify', { email: email, lang: sfLocale() }]);
        window.klaviyo.push(['track', 'Claimed Welcome Offer', { Code: PROMO_CODE }]);
      }
    } catch (e) {}
    // Feed the email to the tracking pixel for first-party advanced matching.
    // It runs through the Web Pixel, so it's consent-gated (only forwarded if the shopper allowed marketing).
    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
        var sfSeg = null; try { sfSeg = localStorage.getItem('sf_segment'); } catch (e) {}
        Shopify.analytics.publish('sf_lead', { email: email, lang: sfLocale(), segment: sfSeg || undefined });
      }
    } catch (e) {}
    try { localStorage.setItem('sf_promo_claimed', '1'); } catch (e) {}
    // Flag the cart so the discount Function applies -20% (shows live in cart).
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { welcome_discount: PROMO_CODE } })
    }).then(function () { if (window.SFCart && SFCart.refresh) SFCart.refresh(); });
  }
  window.SFClaimPromo = SFClaimPromo;

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-promo-form]');
    if (!form) return;
    e.preventDefault();
    var input = form.querySelector('input[type="email"]');
    var email = input ? (input.value || '').trim() : '';
    if (!email) return;
    form.classList.add('is-loading');
    var wrap = form.closest('[data-promo]') || form.parentNode;
    SFClaimPromo(email).then(function () {
      var res = wrap ? wrap.querySelector('[data-promo-result]') : null;
      if (res) res.hidden = false;
      form.hidden = true;
    });
  });

  function closePromoPopup() {
    var pop = document.getElementById('PromoPopup');
    if (pop) { pop.classList.remove('is-open'); document.body.classList.remove('promo-open'); }
  }
  document.addEventListener('click', function (e) { if (e.target.closest('[data-promo-close]')) closePromoPopup(); });

  function maybeShowPromoPopup() {
    var pop = document.getElementById('PromoPopup');
    if (!pop) return;
    try {
      if (localStorage.getItem('sf_promo_seen') === '1' || promoClaimed()) return;
      if (localStorage.getItem('sf_cookie_choice') === 'declined') return;
    } catch (e) {}
    setTimeout(function () {
      pop.classList.add('is-open');
      document.body.classList.add('promo-open');
      try { localStorage.setItem('sf_promo_seen', '1'); } catch (e) {}
    }, 6000);
  }

  /* ---------------------------------------------------------------------
     Init
     --------------------------------------------------------------------- */
  document.addEventListener('DOMContentLoaded', function () {
    bindAll(document);
    refreshSdUpgradeMoney();
    $all('[data-sd-upsell]').forEach(function (upsell) {
      publishSdEvent('sd_upsell_viewed', {
        camera_quantity: parseInt(upsell.getAttribute('data-camera-quantity'), 10) || 1,
        source: upsell.getAttribute('data-sd-source') || 'cart_drawer'
      });
    });
    syncSdBundle();
    maybeShowPromoPopup();
  });
  // Re-bind after Shopify section editor re-renders a section
  document.addEventListener('shopify:section:load', function (e) { bindAll(e.target); });
})();


/* SightForest urgency widgets: dispatch-cutoff countdown (14:00 Paris, weekdays),
   welcome-code visual timer (display only — the code itself never expires), and
   the per-product viewers counter. Re-queries the DOM each tick so it survives
   cart drawer re-renders. */
(function () {
  function parisNow() {
    try { return new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Paris' })); } catch (e) { return new Date(); }
  }
  function fmtHM(mins) {
    var h = Math.floor(mins / 60), m = mins % 60;
    return h > 0 ? h + ' h ' + (m < 10 ? '0' : '') + m : m + ' min';
  }
  function tick() {
    var now = parisNow();
    var wk = now.getDay() >= 1 && now.getDay() <= 5;
    var mins = now.getHours() * 60 + now.getMinutes();
    var before = wk && mins < 840; /* 14:00 */
    document.querySelectorAll('[data-sf-cutoff]').forEach(function (el) {
      if (before) {
        el.textContent = (el.getAttribute('data-tpl-today') || '').replace('[time]', fmtHM(840 - mins));
        el.hidden = false;
      } else {
        var late = el.getAttribute('data-tpl-late') || '';
        el.textContent = late;
        el.hidden = !late;
      }
    });
    document.querySelectorAll('[data-sf-cutoff-label]').forEach(function (el) {
      el.textContent = before ? (el.getAttribute('data-tpl-today') || '') : (el.getAttribute('data-tpl-late') || '');
    });
    document.querySelectorAll('[data-sf-cutoff-time]').forEach(function (el) {
      if (before) {
        var secs = (840 - mins) * 60 - now.getSeconds();
        var p = function (n) { return (n < 10 ? '0' : '') + n; };
        el.textContent = p(Math.floor(secs / 3600)) + 'h ' + p(Math.floor(secs / 60) % 60) + 'm ' + p(secs % 60) + 's';
        el.hidden = false;
      } else { el.hidden = true; }
    });
    document.querySelectorAll('[data-sf-viewers-n]').forEach(function (el) {
      var seed = parseInt(el.getAttribute('data-seed'), 10) || 1;
      var slot = Math.floor(Date.now() / 45000);
      el.textContent = String(3 + ((seed + slot * 7) % 4));
    });
    document.querySelectorAll('[data-sf-viewers]').forEach(function (el) {
      var seed = parseInt(el.getAttribute('data-seed'), 10) || 1;
      var slot = Math.floor(Date.now() / 45000);
      var n = 3 + ((seed + slot * 7) % 4);
      el.textContent = (el.getAttribute('data-tpl') || '').replace('[count]', n);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', tick); else tick();
  setInterval(tick, 1000);
})();
